import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiCheck, FiSave } from "react-icons/fi";
import { completeDevotionEntry, saveDevotionEntry, setSaveStatus } from "../../store/devotionSlice";

const fields = [
  ["stands_out", "What stands out to me?"],
  ["god_is_showing", "What is God showing me?"],
  ["response", "How will I respond?"],
  ["prayer", "Prayer"],
  ["diary", "Additional diary"],
];

const emptyDraft = {
  stands_out: "",
  god_is_showing: "",
  response: "",
  prayer: "",
  diary: "",
  revision: null,
};

const NEU_RAISED_MD =
  "shadow-[-8px_-8px_18px_var(--outer-shadow-light),8px_8px_18px_var(--outer-shadow-dark)]";
const PAGE_RAISED_SM =
  "shadow-[-4px_-4px_9px_var(--outer-shadow-light),4px_4px_9px_var(--outer-shadow-dark)]";

const formatCompletedAt = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const now = new Date();
  const sameDay = dt.toDateString() === now.toDateString();
  return sameDay
    ? dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : dt.toLocaleDateString([], { month: "short", day: "numeric" });
};

const DevotionEditor = ({ passage, entry, user }) => {
  const dispatch = useDispatch();
  const { saveStatus, conflict } = useSelector((state) => state.devotions);
  const [draft, setDraft] = useState(emptyDraft);
  const [dirty, setDirty] = useState(false);
  const [completeStatus, setCompleteStatus] = useState("");
  const storageKey = useMemo(
    () => (user && passage ? `devotionDraft:${user.id}:${passage.id}` : null),
    [user, passage]
  );
  const isComplete = Boolean(entry?.is_complete);
  const completedAtLabel = formatCompletedAt(entry?.completed_at);

  useEffect(() => {
    if (!passage || !user) return;
    const serverDraft = { ...emptyDraft, ...(entry || {}), revision: entry?.revision || null };
    const saved = storageKey ? localStorage.getItem(storageKey) : null;
    setDraft(saved ? { ...serverDraft, ...JSON.parse(saved) } : serverDraft);
    setDirty(false);
  }, [entry, passage, storageKey, user]);

  useEffect(() => {
    if (!dirty || !passage || !user || conflict) return undefined;
    const timer = setTimeout(async () => {
      dispatch(setSaveStatus("saving"));
      const result = await dispatch(
        saveDevotionEntry({ dailyPassageId: passage.id, entry: draft })
      );
      if (saveDevotionEntry.fulfilled.match(result)) {
        localStorage.removeItem(storageKey);
        setDraft((current) => ({ ...current, revision: result.payload.revision }));
        setDirty(false);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [conflict, dirty, dispatch, draft, passage, storageKey, user]);

  const cardClass = `rounded-[2rem] bg-canvas-base p-space-lg md:p-space-xl ${NEU_RAISED_MD}`;

  if (!user) {
    return (
      <section className={cardClass}>
        <div className="flex flex-col gap-space-xs">
          <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
            Personal Reflection
          </span>
          <h2 className="font-noto-serif text-headline-md text-neu-primary">My Reflection</h2>
        </div>
        <p className="mt-space-md text-body-lg text-slate-muted">
          Log in to write your private reflection, diary, and prayer.
        </p>
      </section>
    );
  }

  const updateField = (field, value) => {
    const next = { ...draft, [field]: value };
    setDraft(next);
    setDirty(true);
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const saveStatusText =
    saveStatus === "saving"
      ? "Saving…"
      : saveStatus === "saved"
      ? "Saved"
      : saveStatus === "error"
      ? "Could not save"
      : "";

  const statusText = saveStatusText || completeStatus;

  const toggleComplete = async () => {
    const nextValue = !isComplete;
    const result = await dispatch(
      completeDevotionEntry({ dailyPassageId: passage.id, isComplete: nextValue })
    );
    if (completeDevotionEntry.fulfilled.match(result)) {
      setCompleteStatus(nextValue ? "Marked complete" : "Marked incomplete");
      setTimeout(() => setCompleteStatus(""), 2500);
    }
  };

  return (
    <section className={cardClass}>
      <div className="flex flex-col gap-space-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-space-xs">
          <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
            Personal Reflection
          </span>
          <h2 className="font-noto-serif text-headline-md text-neu-primary">My Reflection</h2>
          {isComplete && (
            <span className="mt-1 inline-flex self-start items-center gap-space-xs rounded-full bg-accent-cta/10 text-accent-cta px-3 py-1 text-label-sm font-semibold">
              <FiCheck aria-hidden="true" />
              Completed{completedAtLabel ? ` · ${completedAtLabel}` : ""}
            </span>
          )}
        </div>
        <div
          aria-live="polite"
          className="text-label-md font-semibold text-slate-muted min-h-[1.5rem]"
        >
          {statusText}
        </div>
      </div>

      {conflict && (
        <div className="mt-space-md rounded-[1rem] p-space-md bg-[#fdecc8] text-[#854a00] text-body-md shadow-[inset_2px_2px_4px_rgba(0,0,0,0.06),inset_-2px_-2px_4px_rgba(255,255,255,0.6)]">
          This reflection changed somewhere else. Refresh the page before saving again.
        </div>
      )}

      <div className="mt-space-lg grid gap-space-md">
        {fields.map(([field, label]) => (
          <label key={field} className="grid gap-space-xs">
            <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
              {label}
            </span>
            <textarea
              value={draft[field] || ""}
              onChange={(event) => updateField(field, event.target.value)}
              rows={field === "diary" ? 5 : 3}
              className="w-full rounded-[1rem] bg-[#eef2f7] border-none p-4 text-body-lg text-scripture-ink leading-8 placeholder:text-slate-muted shadow-[inset_3px_3px_6px_rgba(0,0,0,0.10),inset_-4px_-4px_9px_rgba(255,255,255,1)] focus:outline-none focus:shadow-[inset_3px_3px_6px_rgba(0,0,0,0.12),inset_-4px_-4px_9px_rgba(255,255,255,1),0_0_0_2px_color-mix(in_srgb,var(--accent-cta)_35%,transparent)] transition-shadow resize-y"
            />
          </label>
        ))}
      </div>

      <div className="mt-space-lg flex flex-row items-stretch sm:items-center sm:flex-wrap gap-3 sm:gap-space-md">
        <button
          type="button"
          onClick={() =>
            dispatch(saveDevotionEntry({ dailyPassageId: passage.id, entry: draft }))
          }
          className="flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-space-xs h-11 px-3 sm:px-space-lg rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_12px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] transition-all"
        >
          <FiSave aria-hidden="true" />
          Save
        </button>
        <button
          type="button"
          onClick={toggleComplete}
          aria-pressed={isComplete}
          title={isComplete ? "Click to undo" : undefined}
          className={
            isComplete
              ? "flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-space-xs h-11 px-3 sm:px-space-lg rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[inset_2px_2px_5px_rgba(0,0,0,0.25),inset_-2px_-2px_5px_rgba(255,255,255,0.15)] hover:bg-accent-cta-hover transition-all"
              : `flex-1 sm:flex-none min-w-0 inline-flex items-center justify-center gap-space-xs h-11 px-3 sm:px-space-lg rounded-full bg-canvas-base text-neu-primary text-label-md font-semibold ${PAGE_RAISED_SM} hover:shadow-[-2px_-2px_5px_var(--outer-shadow-light),2px_2px_5px_var(--outer-shadow-dark)] active:shadow-[inset_2px_2px_5px_var(--outer-shadow-dark),inset_-2px_-2px_5px_var(--outer-shadow-light)] transition-all`
          }
        >
          <FiCheck aria-hidden="true" className={isComplete ? "text-white" : "text-accent-cta"} />
          {isComplete ? "Completed" : "Mark Complete"}
        </button>
      </div>
    </section>
  );
};

export default DevotionEditor;
